import React from "react";
import {withTranslation} from "react-i18next";

const CHANNEL_MODIFIERS = {
    'FACEBOOK_CHANNEL': '-facebook',
    'GOOGLE_CHANNEL': '-google',
    'TIKTOK_CHANNEL': '-tiktok',
    'LINKEDIN_CHANNEL': '-linkedin',
    'ANALYTICS_CHANNEL': '-ga4',
};

/** Si la fila trae el desglose agrupado por cuenta, o hay que caer al diccionario plano. */
export function hasAccountDetail(conversionsByAccount) {
    return Array.isArray(conversionsByAccount) && conversionsByAccount.length > 0;
}

function accountHasSomethingToShow(account) {
    return (account.events || []).some(event => event.value > 0) || Boolean(account.status);
}

function positiveEventsOf(valuesByType) {
    return Object.entries(valuesByType || {}).filter(([, value]) => typeof value === "number" && value > 0);
}

/** Si hay algo para dibujar. Lo consulta quien monta el contenedor: un bloque vacío con su título o
 *  su línea divisoria se lee como "no hay datos", que es distinto de "no hay nada que aclarar". */
export function hasDetailToShow({conversionsByAccount, valueByType, notInTotalValueByType}) {
    if (hasAccountDetail(conversionsByAccount)) {
        return conversionsByAccount.some(accountHasSomethingToShow);
    }
    return positiveEventsOf(valueByType).length > 0 || positiveEventsOf(notInTotalValueByType).length > 0;
}

/**
 * El detalle acumulado de conversiones: qué cuenta midió cada evento, cuáles alimentan el total y
 * cuáles no.
 *
 * Vive aparte porque lo muestran DOS columnas —Conversiones y Objetivo 1/2— sobre el mismo dato.
 * Mientras cada una lo dibujaba por su cuenta, contaban la misma historia de dos maneras distintas.
 */
export class ConversionsAccountsDetailClass extends React.Component {
    render() {
        if (!hasAccountDetail(this.props.conversionsByAccount)) {
            return this.renderDetailWithoutAccounts();
        }

        const accounts = this.props.conversionsByAccount.filter(accountHasSomethingToShow);

        return [
            this.renderAccountsBlock(accounts.filter(each => each.in_total), 'in-total'),
            this.renderNotInTotalBlock(accounts.filter(each => !each.in_total)),
        ];
    }

    renderAccountsBlock(accounts, keyPrefix) {
        const {t} = this.props;
        if (accounts.length === 0) {
            return null;
        }
        return (
            <div key={keyPrefix} className="conv-tooltip-block">
                <p className="conv-tooltip-block-title">{t('conversions_alert_tooltip.accumulated_detail')}</p>
                {accounts.map((account, index) => this.renderAccount(account, `${keyPrefix}-${index}`))}
            </div>
        );
    }

    /** Lo que la línea mide pero no entra en el total. Marcado, nunca atenuado: atenuado se lee
     *  como "sin datos", que es el malentendido opuesto. */
    renderNotInTotalBlock(accounts) {
        if (accounts.length === 0) {
            return null;
        }
        return (
            <div key="not-in-total" className="conv-tooltip-block -not-in-total">
                <p className="conv-tooltip-block-title">{this.props.t('conversions_detail.not_in_total_title')}</p>
                {accounts.map((account, index) => this.renderAccount(account, `not-in-total-${index}`))}
                <p className="conv-tooltip-note">{this.notInTotalNote()}</p>
            </div>
        );
    }

    /** Por qué esas conversiones no suman. La fila de una cuenta no trae `conversionsSource` — ahí
     *  se dice lo genérico, sin inventar de dónde sale un total que esta fila no calcula. */
    notInTotalNote() {
        const {t} = this.props;
        const source = this.props.conversionsSource;
        if (!source) {
            return t('conversions_detail.not_in_total_note');
        }
        return source.is_ga4
            ? t('conversions_detail.not_in_total_note_ga4')
            : t('conversions_detail.not_in_total_note_ads');
    }

    renderAccount(account, key) {
        const events = (account.events || []).filter(event => event.value > 0);
        return (
            <div key={key} className="conv-tooltip-account">
                <p className="conv-tooltip-account-head">
                    <span className={`channel-dot ${CHANNEL_MODIFIERS[account.channel] || ''}`}/>
                    <span className="name">{account.account}</span>
                    <span className="total">{this.formatted(account.total)}</span>
                </p>
                {events.map((event, index) => (
                    <p key={`${key}-event-${index}`} className="conv-tooltip-event">
                        <span className="name">{event.name}</span>
                        <span className="value">{this.formatted(event.value)}</span>
                    </p>
                ))}
                {account.status ? <p className="conv-tooltip-account-status">{account.status}</p> : null}
            </div>
        );
    }

    /** Las filas guardadas antes de este cambio no traen el agrupado por cuenta. En vez de dejar el
     *  detalle vacío hasta que el período se reescriba, se muestran los eventos como venían. */
    renderDetailWithoutAccounts() {
        return [
            this.renderLegacyBlock(this.props.valueByType, 'legacy-detail', false),
            this.renderLegacyBlock(this.props.notInTotalValueByType, 'legacy-not-in-total', true),
        ];
    }

    renderLegacyBlock(valuesByType, key, isNotInTotal) {
        const {t} = this.props;
        const events = positiveEventsOf(valuesByType);
        if (events.length === 0) {
            return null;
        }
        const titleKey = isNotInTotal
            ? 'conversions_detail.not_in_total_title'
            : 'conversions_alert_tooltip.accumulated_detail';
        return (
            <div key={key} className={`conv-tooltip-block${isNotInTotal ? ' -not-in-total' : ''}`}>
                <p className="conv-tooltip-block-title">{t(titleKey)}</p>
                {events.map(([name, value]) => (
                    <p key={name} className="conv-tooltip-event">
                        <span className="name">{name}</span>
                        <span className="value">{this.formatted(value)}</span>
                    </p>
                ))}
                {isNotInTotal ? <p className="conv-tooltip-note">{this.notInTotalNote()}</p> : null}
            </div>
        );
    }

    formatted(value) {
        return typeof value === "number" ? value.toFixed(2) : value;
    }
}

export const ConversionsAccountsDetail = withTranslation()(ConversionsAccountsDetailClass);
export default ConversionsAccountsDetail;
